import type { Phrase, Folder } from './types';

export const SAMPLE_PHRASES: Phrase[] = [
  {
    id: 'htn-inpatient',
    name: 'Hypertension (inpatient)',
    trigger: '.htninp',
    tags: ['Inpatient', 'Cardiology'],
    primaryTag: 'inpatient',
    favorite: true,
    pinned: true,
    updated: '2 days ago',
    summary:
      '2017 ACC/AHA + 2023 ESC/HFSA updates. Asymptomatic inpatient HTN is generally NOT an emergency — avoid IV antihypertensives without end-organ damage.',
    sections: [
      {
        title: 'Definitions',
        body:
`Hypertensive urgency:   SBP ≥180 OR DBP ≥120 WITHOUT acute end-organ damage
Hypertensive emergency: SBP ≥180 OR DBP ≥120 WITH acute end-organ damage
"Asymptomatic in-hospital HTN" (no end-organ dysfunction) → typically does NOT
require IV therapy; oral titration or no change.`,
      },
      {
        title: 'Hypertensive urgency',
        body:
`# Hypertensive urgency (no end-organ damage)
BP ***/***. Patient asymptomatic; no chest pain, dyspnea, neuro deficits.
No signs end-organ damage: troponin neg, Cr at baseline, no AMS, no papilledema.
Plan:
- Confirm BP with appropriately-sized cuff, both arms, after 5 min rest
- Address modifiable causes: pain, anxiety, full bladder, withdrawal, missed meds
- Resume home antihypertensives if held; restart or up-titrate
- Avoid IV antihypertensives or PRN aggressive lowering
- Target gradual reduction over 24–48h to <160/100
- DO NOT acutely lower BP >25% — risk of cerebral/coronary ischemia
- F/u PCP within 1 week with home BP log`,
      },
    ],
    references: ['ACC/AHA 2017 Guideline', '2023 ACC HTN management update'],
  },
  {
    id: 'chf-discharge',
    name: 'CHF Discharge Plan',
    trigger: '.chfd',
    tags: ['Inpatient', 'Cardiology'],
    primaryTag: 'inpatient',
    favorite: true,
    updated: '1 week ago',
    summary:
      'Guideline-directed medical therapy at discharge for HFrEF. The four pillars: ARNI/ACEi, β-blocker, MRA, SGLT2i.',
    sections: [
      {
        title: 'Discharge medications',
        body:
`# CHF discharge — GDMT
1. ARNI (sacubitril/valsartan) — start 24/26 mg BID, titrate q2wk
2. β-blocker (carvedilol / metoprolol succinate / bisoprolol)
3. MRA (spironolactone 12.5–25 mg, eplerenone 25 mg)
4. SGLT2i (dapagliflozin 10 mg, empagliflozin 10 mg)
Diuretic: ***
Daily weights, fluid restriction 2L, Na <2g.`,
      },
    ],
    references: ['2022 AHA/ACC/HFSA Heart Failure Guideline'],
  },
  {
    id: 'dka-mgmt',
    name: 'DKA Management',
    trigger: '.dka',
    tags: ['ED', 'Endocrine'],
    primaryTag: 'ed',
    favorite: false,
    updated: '3 days ago',
    summary: 'Initial management of diabetic ketoacidosis in the emergency department.',
    sections: [
      {
        title: 'Diagnosis',
        body:
`DKA criteria:
- Glucose >250 mg/dL
- Anion gap >12
- pH <7.30 or HCO3 <18
- Positive ketones (β-hydroxybutyrate preferred)`,
      },
    ],
    references: ['ADA Standards of Care'],
  },
  {
    id: 'physical-exam-normal',
    name: 'Physical Exam — Normal Adult',
    trigger: '.pex',
    tags: ['Examination', 'General'],
    primaryTag: 'exam',
    favorite: true,
    updated: '2 weeks ago',
    summary: 'Standard normal physical exam template, head to toe.',
    sections: [
      {
        title: 'General',
        body:
`General: A&O ×3, NAD, well-appearing, dressed appropriately.
HEENT: NCAT, PERRLA, EOMI, OP clear, MMM, no LAD, supple neck.
CV: RRR, normal S1/S2, no m/r/g, no JVD, no LE edema.
Lungs: CTAB, no w/r/r, normal effort.
Abdomen: soft, NT, ND, +BS, no organomegaly.
Neuro: A&O×3, CN II–XII intact, 5/5 strength all 4 extremities, sensation intact.
Skin: warm, dry, no rashes or lesions.`,
      },
    ],
    references: [],
  },
  {
    id: 'asthma-outpatient',
    name: 'Asthma (outpatient)',
    trigger: '.asthma',
    tags: ['Outpatient', 'Pulm'],
    primaryTag: 'outpatient',
    favorite: false,
    updated: '3 weeks ago',
    summary: 'Step-up therapy per GINA 2024 for adults with persistent asthma.',
    sections: [
      {
        title: 'Stepwise therapy',
        body:
`Step 1: SABA PRN (or low-dose ICS-formoterol PRN)
Step 2: Daily low-dose ICS
Step 3: Low-dose ICS-LABA
Step 4: Medium-dose ICS-LABA
Step 5: Add-on biologic (anti-IgE, anti-IL5)`,
      },
    ],
    references: ['GINA 2024 guidelines'],
  },
];

// Fallback folder tree — mirrors the real markdown taxonomy (src/markdown):
// inpatient/outpatient/ed have no subfolders; examination has exam/proc/notes.
export const SAMPLE_FOLDERS: Folder[] = [
  {
    id: 'inpatient',
    name: 'Inpatient',
    color: 'var(--tag-inpatient-fg)',
    kind: 'inpatient',
    count: 261,
  },
  {
    id: 'outpatient',
    name: 'Outpatient',
    color: 'var(--tag-outpatient-fg)',
    kind: 'outpatient',
    count: 343,
  },
  {
    id: 'ed',
    name: 'ED',
    color: 'var(--tag-ed-fg)',
    kind: 'ed',
    count: 4,
  },
  {
    id: 'examination',
    name: 'Examination',
    color: 'var(--tag-exam-fg)',
    kind: 'exam',
    count: 83,
    children: [
      { id: 'exam', name: 'Exam', count: 22 },
      { id: 'proc', name: 'Proc', count: 18 },
      { id: 'notes', name: 'Notes', count: 43 },
    ],
  },
];

